import React, { useState } from "react"

import {
	ChatBotFlowBlockRule,
	ChatBotFlowBlockRuleCategory
} from "@/protocols/chatBot"
import { EditableBlockDefaultProps, NextFlowBlockRule } from "@/protocols/chatBotConstructor"

import { PopConfirm } from "@/components"

import BlockBodyItem from "@/pages/Admin/ChatBot/ChatBotConstructor/components/BlockBodyItem"
import BlockBodyItemTitle from "@/pages/Admin/ChatBot/ChatBotConstructor/components/BlockBodyItemTitle"
import BlockBodyContainer from "@/pages/Admin/ChatBot/ChatBotConstructor/components/BlockBodyContainer"
import BlockBodyItemInput from "@/pages/Admin/ChatBot/ChatBotConstructor/components/BlockBodyItemInput"
import BlockEditContainer from "@/pages/Admin/ChatBot/ChatBotConstructor/components/BlockEditContainer"
import BlockBodyEditMessageItem from "@/pages/Admin/ChatBot/ChatBotConstructor/components/BlockBodyEditMessageItem"
import BlockConditionEditor, { ValidationSlugInput } from "@/pages/Admin/ChatBot/ChatBotConstructor/components/BlockConditionEditor"
import CreateConditionIconButton from "@/pages/Admin/ChatBot/ChatBotConstructor/components/CreateConditionIconButton"
import NoConditionText from "@/pages/Admin/ChatBot/ChatBotConstructor/components/NoConditionText"

import { generateUUID } from "@/utils/id"
import { getNextChatBotRuleDescription } from "@/utils/chatBot"

const ConditionalTextBlock: React.FC<EditableBlockDefaultProps> = (props) => {
	const {
		changeChatBotFlowContent,
		chatBotFlowBlockContent,
		nextChatBotFlowBlockRules,
		changeNextChatBotFlowBlockRule,
		toggleNextChatBotFlowBlock,
		constructionResources,
		flowBlock,
		deleteChatBotFlowBlock
	} = props

	const [selectedNextFlowBlockRuleId, setSelectedNextFlowBlockRuleId] = useState("")

	const handleSelectNextFlowBlockRule = (nextFlowBlockRule: ChatBotFlowBlockRule) => {
		toggleNextChatBotFlowBlock(nextFlowBlockRule)

		setSelectedNextFlowBlockRuleId(lastSelectedNextFlowBlockRuleId => {
			if (lastSelectedNextFlowBlockRuleId === nextFlowBlockRule.id) {
				return ""
			}

			return nextFlowBlockRule.id
		})
	}

	const getAvailableSlugInputsByRule = (nextChatBotFlowBlockRule: NextFlowBlockRule): ValidationSlugInput[] => {
		if (nextChatBotFlowBlockRule.category === "continue-condition") {
			return ["message-received"]
		} else {
			return ["max-invalid-responses", "no-interaction-timeout"]
		}
	}

	const handleEditNextChatBotFlowBlockRule = (nextChatBotFlowBlockRule: ChatBotFlowBlockRule) => {
		BlockConditionEditor.open({
			onSave: (chatBotFlowBlockRule) => changeNextChatBotFlowBlockRule("UPDATE", chatBotFlowBlockRule),
			nextChatBotFlowBlockRule: nextChatBotFlowBlockRule,
			constructionResources,
			chatBotFlowBlockType: "conditional-text",
			availableSlugInputs: getAvailableSlugInputsByRule(nextChatBotFlowBlockRule)
		})
	}

	const handleCreateNextChatBotFlowBlockRule = (selectedCategory: ChatBotFlowBlockRuleCategory) => {
		const block = constructionResources.blocks.find(({ type }) => type === "conditional-text")

		const nextChatBotFlowBlockRule = block?.available_next_block_flow_rules?.find(({ category }) => category === selectedCategory)

		if (nextChatBotFlowBlockRule) {
			BlockConditionEditor.open({
				onSave: (chatBotFlowBlockRule) => changeNextChatBotFlowBlockRule("CREATE", chatBotFlowBlockRule),
				nextChatBotFlowBlockRule: {
					...nextChatBotFlowBlockRule,
					id: generateUUID(),
					next_chat_bot_flow_block_id: null,
					validations: nextChatBotFlowBlockRule?.validations || []
				},
				constructionResources,
				chatBotFlowBlockType: "conditional-text",
				availableSlugInputs: getAvailableSlugInputsByRule(nextChatBotFlowBlockRule)
			})
		}
	}

	const handleDeleteNextFlowBlockRule = (nextFlowBlockRule: ChatBotFlowBlockRule) => {
		PopConfirm.open({
			title: "EXCLUIR CONDIÇÃO",
			description: "Tem certeza? Essa ação é irreversível.",
			onConfirm: () => changeNextChatBotFlowBlockRule("DELETE", nextFlowBlockRule),
			confirmButtonText: "EXCLUIR"
		})
	}

	const continueConditionRules = nextChatBotFlowBlockRules.filter(({ category }) => category === "continue-condition")
	const exitConditionRules = nextChatBotFlowBlockRules.filter(({ category }) => category === "exit-condition")

	return (
		<BlockEditContainer
			name={flowBlock.title}
			onDelete={deleteChatBotFlowBlock}
		>
			<BlockBodyContainer>
				<BlockBodyEditMessageItem
					constructionResources={constructionResources}
					title="Mensagem"
					onSave={(buildedMessages) => changeChatBotFlowContent({ messagesToSend: buildedMessages })}
					messages={chatBotFlowBlockContent.messagesToSend}
				/>

				<BlockBodyItem>
					<BlockBodyItemTitle
						title="Condições para seguir"
						endIcon={(
							<CreateConditionIconButton
								onClick={() => handleCreateNextChatBotFlowBlockRule("continue-condition")}
							/>
						)}
					/>

					{continueConditionRules?.length ? (
						continueConditionRules.map((nextChatBotFlowBlockRule) => (
							<BlockBodyItemInput
								onClick={() => handleSelectNextFlowBlockRule(nextChatBotFlowBlockRule)}
								key={nextChatBotFlowBlockRule.id}
								text={getNextChatBotRuleDescription(nextChatBotFlowBlockRule)}
								type="keep-going-condition"
								onEdit={() => handleEditNextChatBotFlowBlockRule(nextChatBotFlowBlockRule)}
								onDelete={() => handleDeleteNextFlowBlockRule(nextChatBotFlowBlockRule)}
								selectable={nextChatBotFlowBlockRule.id !== selectedNextFlowBlockRuleId}
								selected={nextChatBotFlowBlockRule.id === selectedNextFlowBlockRuleId}
							/>
						))
					) : (
						<NoConditionText />
					)}
				</BlockBodyItem>

				<BlockBodyItem>
					<BlockBodyItemTitle
						title="Condições para sair"
						endIcon={(
							<CreateConditionIconButton
								onClick={() => handleCreateNextChatBotFlowBlockRule("exit-condition")}
							/>
						)}
					/>

					{exitConditionRules?.length ? (
						exitConditionRules.map((nextChatBotFlowBlockRule) => (
							<BlockBodyItemInput
								key={nextChatBotFlowBlockRule.id}
								onClick={() => handleSelectNextFlowBlockRule(nextChatBotFlowBlockRule)}
								text={getNextChatBotRuleDescription(nextChatBotFlowBlockRule)}
								type="finish-flow-condition"
								onEdit={() => handleEditNextChatBotFlowBlockRule(nextChatBotFlowBlockRule)}
								onDelete={() => handleDeleteNextFlowBlockRule(nextChatBotFlowBlockRule)}
								selectable={nextChatBotFlowBlockRule.id !== selectedNextFlowBlockRuleId}
								selected={nextChatBotFlowBlockRule.id === selectedNextFlowBlockRuleId}
							/>
						))
					) : (
						<NoConditionText />
					)}
				</BlockBodyItem>

				<BlockBodyEditMessageItem
					constructionResources={constructionResources}
					title="Mensagem quando a resposta não entrar em nenhuma condição"
					onSave={(buildedMessages) => changeChatBotFlowContent({ invalidInputResponseMessages: buildedMessages })}
					messages={chatBotFlowBlockContent.invalidInputResponseMessages}
				/>
			</BlockBodyContainer>
		</BlockEditContainer>
	)
}

export default ConditionalTextBlock
